import type { OfficialChatDto } from '@pxc/shared';
import { useEffect, useState } from 'react';
import { FitText } from '../../components/FitText.js';
import { useT } from '../../i18n/index.js';
import { openExternal } from '../../services/telegram.js';

/**
 * The official channel and group as the way in (Settings in the panel):
 * someone who is not in them gets this instead of the app — each chat to join,
 * the ones already joined marked done, and a button to come back through once
 * they have. The gold button opens the next chat left; back from it, the
 * screen checks on its own and moves on to the one after. The second button
 * is for when Telegram does not say they came back.
 */
export function ChannelGateScreen({ details, onRetry }: { details: unknown; onRetry: () => void }) {
  const { t } = useT();
  const chats = readChats(details);
  const next = chats.find((chat) => !chat.member) ?? null;
  const kinds = new Set(chats.map((chat) => chat.kind));
  const both = kinds.has('channel') && kinds.has('group');
  const groupOnly = !both && kinds.has('group');
  const title = t(both ? 'channel.titleBoth' : groupOnly ? 'channel.titleGroup' : 'channel.title');
  const labelOf = (chat: OfficialChatDto) => t(chat.kind === 'group' ? 'channel.groupLabel' : 'channel.ruleLabel');
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!joining) return undefined;
    const back = () => {
      if (document.visibilityState === 'visible') onRetry();
    };
    const telegram = (window as unknown as {
      Telegram?: { WebApp?: { onEvent?(name: string, handler: () => void): void; offEvent?(name: string, handler: () => void): void } };
    }).Telegram?.WebApp;
    document.addEventListener('visibilitychange', back);
    telegram?.onEvent?.('activated', back);
    return () => {
      document.removeEventListener('visibilitychange', back);
      telegram?.offEvent?.('activated', back);
    };
  }, [joining, onRetry]);

  return (
    <div className="ban" role="alert">
      <div className="ban__frame">
        <div className="ban__stage">
          <h1 className="visually-hidden">{title}</h1>
          <div className="ban__plate ban__plate--live" aria-hidden>
            <FitText className="ban__plate-text">{title}</FitText>
          </div>
        </div>

        <section className="ban__panel">
          <p className="ban__lead">{t('channel.lead')}</p>
          <p className="ban__why">{t(both ? 'channel.bodyBoth' : groupOnly ? 'channel.bodyGroup' : 'channel.body')}</p>
          {chats.length > 1 ? (
            // Both asked for: one rule, a line for each, and a tick on the one joined.
            <ul className="ban__rule ban__chats">
              {chats.map((chat) => (
                <li key={`${chat.kind}:${chat.chat}`} className={`ban__chat${chat.member ? ' is-done' : ''}`}>
                  <span className="ban__rule-label">{labelOf(chat)}</span>
                  <bdi className="ban__chat-name">{chat.chat || chat.url}</bdi>
                  <span className="ban__chat-state" aria-hidden>
                    {chat.member ? '✓' : ''}
                  </span>
                  {chat.member ? <span className="visually-hidden">{t('channel.done')}</span> : null}
                </li>
              ))}
            </ul>
          ) : chats[0] ? (
            <figure className="ban__rule">
              <figcaption className="ban__rule-label">{labelOf(chats[0])}</figcaption>
              <blockquote className="ban__rule-text">
                <bdi>{chats[0].chat || chats[0].url}</bdi>
              </blockquote>
            </figure>
          ) : null}
        </section>

        <div className="ban__actions">
          {next?.url ? (
            <button
              type="button"
              className="ban__primary"
              onClick={() => {
                setJoining(true);
                openExternal(next.url);
              }}
            >
              <FitText as="span" className="ban__primary-text">
                {t(next.kind === 'group' ? 'channel.joinGroup' : 'channel.join')}
              </FitText>
            </button>
          ) : null}
          <button type="button" className="ban__secondary" onClick={onRetry}>
            {t('channel.check')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The chats the refusal names (ChannelGateDetailsDto). A server from before
 * the group sent the channel alone, as `url` and `chat`.
 */
function readChats(details: unknown): OfficialChatDto[] {
  const { url, chat, chats } = (details ?? {}) as { url?: unknown; chat?: unknown; chats?: unknown };
  if (Array.isArray(chats)) {
    return chats.flatMap((entry: unknown) => {
      const item = (entry ?? {}) as Record<string, unknown>;
      if (item.kind !== 'channel' && item.kind !== 'group') return [];
      const name = typeof item.chat === 'string' ? item.chat : '';
      const link = typeof item.url === 'string' ? item.url : '';
      if (!name && !link) return [];
      return [{ kind: item.kind, chat: name, url: link, member: item.member === true }];
    });
  }
  const link = typeof url === 'string' ? url : '';
  const name = typeof chat === 'string' ? chat : '';
  return link || name ? [{ kind: 'channel', chat: name, url: link, member: false }] : [];
}
